/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import { IFieldDefinitionPanelToggleData, IFieldDefinitionPanelToggleState } from ".";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { BaseCommands, FormErrors, IFieldChangeData } from "../../State";
import { CmdBuilder } from "@nuance/hap-components";

const logger: ILogger = getLogger();

export class FieldDefinitionPanelToggleCommands extends BaseCommands<
    IFieldDefinitionPanelToggleData,
    IFieldDefinitionPanelToggleState
> {
    constructor() {
        super();
        const cb = new CmdBuilder();
        const cmds = {
            onBlur: cb.bindCmd<IFieldDefinitionPanelToggleState, string>(this.OnBlurHandler),
            onTextChanged: cb.bindCmd<IFieldDefinitionPanelToggleState, IFieldChangeData>(
                this.OnTextChangedHandler
            ),
            onDefaultValueChanged: cb.bindCmd(this.onDefaultValueChangedHandler),
            onAccessLevelChanged: cb.bindCmd(this.OnAccessLevelChangedHandler),
            onHelpTextChange: cb.bindCmd(this.OnHelpTextChangeHandler),
            onAuditChanged: cb.bindCmd(this.OnAuditChangedHandler),
            onIncludeToggleLabelsChanged: cb.bindCmdNP(this.onIncludeToggleLabelsChangedHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer<IFieldDefinitionPanelToggleState>(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    Save = (state: IFieldDefinitionPanelToggleState): IFieldDefinitionPanelToggleData => {
        logger.logInformation("Saving toggle properties");

        // important to make a copy!
        return { ...state.data };
    };

    /**
     * Validates the form fields.
     *
     * @param state Current state (immutable)
     * @returns Form errors
     */
    Validate(state: IFieldDefinitionPanelToggleState): FormErrors<IFieldDefinitionPanelToggleData> {
        const errors: FormErrors<IFieldDefinitionPanelToggleData> = {};

        // settingName validation
        if (state.data.settingName === "") {
            errors.settingName = "Setting name is required.";
        } else {
            if (state.common.validateSettingName === undefined) {
                logger.logError("Setting name validation function not hooked up");
                throw new Error("Unable to validate setting name");
            }

            errors.settingName = state.common.validateSettingName(
                state.data.settingName,
                state.common.originalSettingName
            );
        }

        if (state.data.title === "") {
            errors.title = "Label is required.";
        }

        if (!state.data.helpTextValid) {
            errors.helpTextValid = "Help text is not valid";
        }

        if (state.data.includeToggleLabels) {
            if (state.data.offLabel === "") {
                errors.offLabel = "Off label is required";
            }
            if (state.data.onLabel === "") {
                errors.onLabel = "On label is required";
            }
        }

        return errors;
    }

    /////////////////////////////////////////////////////////////////
    // Command handlers
    /////////////////////////////////////////////////////////////////

    onDefaultValueChangedHandler = (state: IFieldDefinitionPanelToggleState, newValue: boolean) => {
        state.isDirty = true;
        state.data.defaultValue = newValue;
        return state;
    };

    onIncludeToggleLabelsChangedHandler = (state: IFieldDefinitionPanelToggleState) => {
        state.data.includeToggleLabels = !state.data.includeToggleLabels;
        state.isDirty = true;
        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);
        return state;
    };
}
